if (!customElements.get('free-shipping-bar')) {
  const previousTotals = new Map();
  const stateLifetime = 5000;

  class FreeShippingBar extends HTMLElement {
    constructor() {
      super();

      this.thresholdCents = parseInt(this.dataset.thresholdCents, 10);
      this.currentTotal = parseInt(this.dataset.currentTotal, 10) || 0;
      this.remainingTemplate = this.dataset.remainingTemplate || '[amount] to free shipping';
      this.unlockedText = this.dataset.unlockedText || 'Free shipping unlocked';
      this.moneyFormat = this.dataset.moneyFormat || '€{{amount}}';
      this.stateKey = [this.dataset.instance, this.dataset.country, this.thresholdCents].join(':');

      this.textElement = this.querySelector('.free-shipping-bar__text');
      this.progressBar = this.querySelector('.free-shipping-bar__progress');
      this.activeBar = this.querySelector('.free-shipping-bar__progress-active');
    }

    connectedCallback() {
      const previousState = previousTotals.get(this.stateKey);
      const canAnimateFromPrevious =
        previousState && Date.now() - previousState.updatedAt < stateLifetime && previousState.total !== this.currentTotal;

      this.updateBar(this.currentTotal, canAnimateFromPrevious ? previousState.total : undefined);
      previousTotals.delete(this.stateKey);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
      }
    }

    disconnectedCallback() {
      const totalBeforeReplacement =
        typeof this.previousTotal === 'number' ? this.previousTotal : this.currentTotal;
      previousTotals.set(this.stateKey, { total: totalBeforeReplacement, updatedAt: Date.now() });

      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
    }

    onCartUpdate(eventData) {
      if (!eventData || !eventData.cartData || typeof eventData.cartData.total_price === 'undefined') return;

      const total = eventData.cartData.item_count === 0 ? 0 : eventData.cartData.total_price;
      this.previousTotal = this.currentTotal;
      this.updateBar(total);

      window.clearTimeout(this.previousTotalTimeout);
      this.previousTotalTimeout = window.setTimeout(() => {
        this.previousTotal = undefined;
      }, 500);
    }

    updateBar(currentTotal, previousTotal) {
      if (!this.thresholdCents || this.thresholdCents <= 0) return;

      this.currentTotal = currentTotal;
      const isUnlocked = currentTotal >= this.thresholdCents;
      const percentage = isUnlocked ? 100 : this.percentageFor(currentTotal);
      const text = isUnlocked
        ? this.unlockedText
        : this.remainingTemplate.replace('[amount]', this.formatMoney(this.thresholdCents - currentTotal));

      this.classList.toggle('is-unlocked', isUnlocked);
      if (this.textElement) this.textElement.textContent = text;
      if (this.progressBar) {
        this.progressBar.setAttribute('aria-valuenow', percentage.toString());
        this.progressBar.setAttribute('aria-label', text);
      }

      if (!this.activeBar) return;

      if (typeof previousTotal === 'number' && !this.prefersReducedMotion()) {
        this.activeBar.style.transition = 'none';
        this.activeBar.style.width = `${this.percentageFor(previousTotal)}%`;
        this.activeBar.offsetWidth;
        this.activeBar.style.removeProperty('transition');
        requestAnimationFrame(() => {
          this.activeBar.style.width = `${percentage}%`;
        });
        return;
      }

      this.activeBar.style.width = `${percentage}%`;
    }

    percentageFor(total) {
      return Math.min(100, Math.max(0, Math.round((total / this.thresholdCents) * 100)));
    }

    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, this.moneyFormat);
      }

      const format = this.moneyFormat || '€{{amount}}';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholderRegex);
      const matchType = match ? match[1] : 'amount_with_comma_separator';
      const precision = matchType.includes('no_decimals') ? 0 : 2;
      const usesCommaDecimal = matchType === 'amount' || matchType === 'amount_no_decimals';
      const thousands = usesCommaDecimal ? ',' : '.';
      const decimal = usesCommaDecimal ? '.' : ',';
      const value = this.formatWithDelimiters(cents, precision, thousands, decimal);

      return format.replace(placeholderRegex, value);
    }

    formatWithDelimiters(cents, precision, thousands, decimal) {
      const fixed = (cents / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const groupedWhole = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${groupedWhole}${decimal}${fraction}` : groupedWhole;
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
}
